import React, { useState, useEffect, useMemo } from 'react';
import {
  RefreshCw,
  Plus,
  Edit,
  Trash2,
  AlertCircle,
  Eye,
  EyeOff,
  Globe,
  Search,
  Play,
  Pause,
  Save,
  X,
  CheckCircle,
} from 'lucide-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { format } from 'date-fns';
import { newsTickerService } from '../services/api';

// ── Simple inline toast ───────────────────────────────────────────────────────
const Toast = ({ message, type, onClose }) => (
  <div
    className={`fixed bottom-6 right-6 z-[9999] flex items-center space-x-3 px-5 py-3 rounded-xl shadow-xl text-white text-sm font-medium transition-all duration-300 ${
      type === 'success' ? 'bg-green-600' : type === 'error' ? 'bg-red-600' : 'bg-gray-800'
    }`}
  >
    {type === 'success' ? (
      <CheckCircle className="h-5 w-5 flex-shrink-0" />
    ) : (
      <AlertCircle className="h-5 w-5 flex-shrink-0" />
    )}
    <span>{message}</span>
    <button onClick={onClose} className="ml-2 opacity-70 hover:opacity-100">
      <X className="h-4 w-4" />
    </button>
  </div>
);

const useToast = () => {
  const [toast, setToast] = useState(null);
  const show = (message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 4000);
  };
  return { toast, show };
};
// ─────────────────────────────────────────────────────────────────────────────

const getStatusColor = (status) => {
  switch ((status || '').toLowerCase()) {
    case 'active':    return 'bg-green-100 text-green-800';
    case 'inactive':  return 'bg-gray-100 text-gray-800';
    case 'scheduled': return 'bg-blue-100 text-blue-800';
    case 'archived':  return 'bg-purple-100 text-purple-800';
    default:          return 'bg-gray-100 text-gray-800';
  }
};

const NewsTickerManagement = () => {
  const queryClient = useQueryClient();
  const { toast, show: showToast } = useToast();

  const [searchTerm, setSearchTerm]       = useState('');
  const [statusFilter, setStatusFilter]   = useState('all');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal]     = useState(false);
  const [selectedNews, setSelectedNews]       = useState(null);
  const [bulkSelection, setBulkSelection]     = useState([]);

  const emptyNews = { title: '', content: '', link: '', is_breaking: false, order: 0, expiry_date: '' };
  const [newNews, setNewNews] = useState(emptyNews);

  // ── Queries ─────────────────────────────────────────────────────────────────
  const { data: newsData, isLoading, error, refetch } = useQuery({
    queryKey: ['newsTicker'],
    queryFn: async () => {
      const data = await newsTickerService.getAll();
      if (Array.isArray(data))           return data;
      if (Array.isArray(data?.results))  return data.results;
      if (Array.isArray(data?.data))     return data.data;
      return [];
    },
    refetchOnWindowFocus: false,
  });

  const { data: tickerStatus, refetch: refetchStatus } = useQuery({
    queryKey: ['tickerStatus'],
    queryFn: async () => {
      try {
        return await newsTickerService.getStatus();
      } catch {
        return { enabled: false };
      }
    },
    // Poll every 10s so the status card stays current
    refetchInterval: 10000,
  });

  // ── Derived data ─────────────────────────────────────────────────────────────
  const newsArray = useMemo(() => (Array.isArray(newsData) ? newsData : []), [newsData]);

  const filteredNews = useMemo(() => {
    return newsArray.filter((n) => {
      if (!n?.id) return false;
      const matchSearch =
        !searchTerm ||
        (n.title || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (n.content || '').toLowerCase().includes(searchTerm.toLowerCase());
      const matchStatus = statusFilter === 'all' || n.status === statusFilter;
      return matchSearch && matchStatus;
    });
  }, [newsArray, searchTerm, statusFilter]);

  const totalNews    = newsArray.length;
  const activeNews   = newsArray.filter((n) => n?.status === 'active').length;
  const breakingNews = newsArray.filter((n) => n?.is_breaking).length;

  // ── Mutations ────────────────────────────────────────────────────────────────
  const createMutation = useMutation({
    mutationFn: (data) => newsTickerService.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['newsTicker'] });
      setShowCreateModal(false);
      setNewNews(emptyNews);
      showToast('News item created successfully');
    },
    onError: (err) => showToast(`Failed to create: ${err.message}`, 'error'),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }) => newsTickerService.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['newsTicker'] });
      setShowEditModal(false);
      setSelectedNews(null);
      showToast('News item updated successfully');
    },
    onError: (err) => showToast(`Failed to update: ${err.message}`, 'error'),
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => newsTickerService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['newsTicker'] });
      showToast('News item deleted');
    },
    onError: (err) => showToast(`Failed to delete: ${err.message}`, 'error'),
  });

  const toggleTickerMutation = useMutation({
    mutationFn: (enable) => newsTickerService.toggleTicker(enable),
    onSuccess: (data, enable) => {
      // Immediately update the cached status so the button flips instantly
      queryClient.setQueryData(['tickerStatus'], (old) => ({
        ...(old || {}),
        enabled: enable,
      }));
      queryClient.invalidateQueries({ queryKey: ['newsTicker'] });
      queryClient.invalidateQueries({ queryKey: ['tickerStatus'] });
      showToast(`News ticker ${enable ? 'enabled' : 'disabled'} successfully`);
    },
    onError: (err) => showToast(`Failed to toggle ticker: ${err.message}`, 'error'),
  });

  const bulkUpdateMutation = useMutation({
    mutationFn: ({ ids, status }) => newsTickerService.bulkUpdate({ news_ids: ids, status }),
    onSuccess: (_, { status }) => {
      queryClient.invalidateQueries({ queryKey: ['newsTicker'] });
      setBulkSelection([]);
      showToast(`${bulkSelection.length} items marked as ${status}`);
    },
    onError: (err) => showToast(`Bulk update failed: ${err.message}`, 'error'),
  });

  // ── Handlers ─────────────────────────────────────────────────────────────────
  const handleCreateNews = () => {
    if (!newNews.title.trim()) { showToast('Please enter a news title', 'error'); return; }
    createMutation.mutate(newNews);
  };

  const handleUpdateNews = () => {
    if (!selectedNews?.title) { showToast('Title is required', 'error'); return; }
    updateMutation.mutate({
      id: selectedNews.id,
      data: {
        title:             selectedNews.title,
        content:           selectedNews.content || '',
        link:              selectedNews.link || '',
        status:            selectedNews.status || 'active',
        is_breaking:       selectedNews.is_breaking || false,
        is_ticker_enabled: selectedNews.is_ticker_enabled !== false,
        order:             selectedNews.order || 0,
        expiry_date:       selectedNews.expiry_date || null,
      },
    });
  };

  const handleDeleteNews = (id) => {
    if (window.confirm('Delete this news item?')) deleteMutation.mutate(id);
  };

  const handleToggleTicker = () => {
    const newEnabled = !tickerStatus?.enabled;
    if (window.confirm(`${newEnabled ? 'Enable' : 'Disable'} the news ticker?`)) {
      toggleTickerMutation.mutate(newEnabled);
    }
  };

  const handleToggleSelectAll = () => {
    if (bulkSelection.length === filteredNews.length) {
      setBulkSelection([]);
    } else {
      setBulkSelection(filteredNews.filter((n) => n?.id).map((n) => n.id));
    }
  };

  const handleToggleSelect = (id) => {
    if (!id) return;
    setBulkSelection((prev) => (prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]));
  };

  const handleBulkAction = (status) => {
    if (!bulkSelection.length || !['active', 'inactive', 'archived'].includes(status)) return;
    bulkUpdateMutation.mutate({ ids: bulkSelection, status });
  };

  // ── Loading / error states ───────────────────────────────────────────────────
  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="h-8 w-8 text-gray-400 animate-spin" />
        <span className="ml-3 text-gray-600">Loading news ticker...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <AlertCircle className="h-12 w-12 text-red-400 mx-auto mb-4" />
          <p className="text-gray-600 font-medium">Failed to load news ticker data</p>
          <p className="text-sm text-gray-500 mt-1">{error.message}</p>
          <button
            onClick={() => refetch()}
            className="mt-4 px-4 py-2 bg-gray-100 rounded-lg text-gray-700 hover:bg-gray-200 transition-colors"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  // ── Render ───────────────────────────────────────────────────────────────────
  return (
    <div className="space-y-6">
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => {}} />}

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">News Ticker Management</h1>
          <p className="text-gray-600 mt-1">Manage breaking news and announcements</p>
        </div>

        <div className="flex items-center space-x-3">
          {/* Global enable / disable toggle */}
          <button
            onClick={handleToggleTicker}
            disabled={toggleTickerMutation.isPending}
            className={`flex items-center space-x-2 px-4 py-2.5 rounded-xl font-medium transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed ${
              tickerStatus?.enabled
                ? 'bg-red-100 text-red-700 hover:bg-red-200'
                : 'bg-green-100 text-green-700 hover:bg-green-200'
            }`}
          >
            {toggleTickerMutation.isPending ? (
              <><RefreshCw className="h-4 w-4 animate-spin" /><span>Updating…</span></>
            ) : tickerStatus?.enabled ? (
              <><Pause className="h-4 w-4" /><span>Disable Ticker</span></>
            ) : (
              <><Play className="h-4 w-4" /><span>Enable Ticker</span></>
            )}
          </button>

          <button
            onClick={() => setShowCreateModal(true)}
            className="flex items-center space-x-2 px-4 py-2.5 bg-[#1d1d1d] text-white rounded-xl hover:bg-black transition-all duration-200 font-medium"
          >
            <Plus className="h-4 w-4" />
            <span>Add News</span>
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        {[
          { label: 'Total News',     value: totalNews,    icon: <Globe className="h-8 w-8 text-gray-300" />,            color: 'text-gray-900' },
          { label: 'Active',         value: activeNews,   icon: <Eye className="h-8 w-8 text-green-300" />,             color: 'text-green-600' },
          { label: 'Breaking',       value: breakingNews, icon: <AlertCircle className="h-8 w-8 text-red-300" />,       color: 'text-red-600' },
          {
            label: 'Ticker Status',
            value: tickerStatus?.enabled ? 'Live' : 'Off',
            icon: tickerStatus?.enabled
              ? <Play className="h-8 w-8 text-green-300" />
              : <Pause className="h-8 w-8 text-red-300" />,
            color: tickerStatus?.enabled ? 'text-green-600' : 'text-red-600',
          },
        ].map((s) => (
          <div key={s.label} className="bg-white rounded-2xl border border-gray-200 p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500 font-medium">{s.label}</p>
                <p className={`text-2xl font-bold mt-1 ${s.color}`}>{s.value}</p>
              </div>
              {s.icon}
            </div>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="bg-white rounded-2xl border border-gray-200 p-5">
        <div className="flex flex-col md:flex-row md:items-center justify-between space-y-4 md:space-y-0">
          <div className="flex items-center space-x-4">
            <div className="relative md:w-64">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search news…"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#1d1d1d]"
              />
            </div>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-4 py-2.5 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#1d1d1d]"
            >
              <option value="all">All Status</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
              <option value="scheduled">Scheduled</option>
              <option value="archived">Archived</option>
            </select>
          </div>

          {bulkSelection.length > 0 && (
            <div className="flex items-center space-x-2">
              <span className="text-sm text-gray-600">{bulkSelection.length} selected</span>
              <select
                onChange={(e) => { if (e.target.value) handleBulkAction(e.target.value); }}
                defaultValue=""
                className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#1d1d1d]"
              >
                <option value="" disabled>Bulk Actions</option>
                <option value="active">Mark Active</option>
                <option value="inactive">Mark Inactive</option>
                <option value="archived">Archive</option>
              </select>
              <button onClick={() => setBulkSelection([])} className="px-3 py-2 text-sm text-gray-600 hover:text-gray-900">
                Clear
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full min-w-full">
            <thead>
              <tr className="bg-gray-50">
                <th className="w-12 px-6 py-4">
                  <input
                    type="checkbox"
                    checked={filteredNews.length > 0 && bulkSelection.length === filteredNews.length}
                    onChange={handleToggleSelectAll}
                    className="h-4 w-4 text-[#1d1d1d] rounded border-gray-300 focus:ring-[#1d1d1d]"
                  />
                </th>
                {['News Title', 'Status', 'Breaking', 'Ticker', 'Published', 'Actions'].map((h) => (
                  <th key={h} className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {filteredNews.length === 0 ? (
                <tr>
                  <td colSpan="7" className="px-6 py-12 text-center text-gray-400">
                    <AlertCircle className="h-12 w-12 mx-auto mb-4" />
                    <p className="text-lg font-medium">No news found</p>
                    <p className="text-sm mt-1">
                      {searchTerm || statusFilter !== 'all'
                        ? 'Try adjusting your filters'
                        : 'Add your first news item to get started'}
                    </p>
                  </td>
                </tr>
              ) : (
                filteredNews.map((news) => {
                  if (!news?.id) return null;
                  const publishDate = news.publish_date ? new Date(news.publish_date) : new Date();
                  const expiryDate  = news.expiry_date  ? new Date(news.expiry_date)  : null;
                  return (
                    <tr key={news.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-6 py-4">
                        <input
                          type="checkbox"
                          checked={bulkSelection.includes(news.id)}
                          onChange={() => handleToggleSelect(news.id)}
                          className="h-4 w-4 text-[#1d1d1d] rounded border-gray-300 focus:ring-[#1d1d1d]"
                        />
                      </td>
                      <td className="px-6 py-4 max-w-sm">
                        <p className="font-medium text-gray-900 truncate">{news.title || 'Untitled'}</p>
                        {news.link && (
                          <a
                            href={news.link}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-sm text-blue-600 hover:text-blue-800 truncate block"
                          >
                            {news.link.length > 50 ? news.link.slice(0, 50) + '…' : news.link}
                          </a>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(news.status)}`}>
                          {news.status ? news.status.charAt(0).toUpperCase() + news.status.slice(1) : 'Unknown'}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        {news.is_breaking
                          ? <AlertCircle className="h-5 w-5 text-red-500" />
                          : <span className="text-gray-400">–</span>}
                      </td>
                      <td className="px-6 py-4">
                        {news.is_ticker_enabled !== false
                          ? <Eye className="h-5 w-5 text-green-500" />
                          : <EyeOff className="h-5 w-5 text-gray-400" />}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-500">
                        <div>{format(publishDate, 'MMM d, yyyy')}</div>
                        {expiryDate && (
                          <div className="text-xs text-red-500">Expires: {format(expiryDate, 'MMM d, yyyy')}</div>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center space-x-2">
                          <button
                            onClick={() => { setSelectedNews(news); setShowEditModal(true); }}
                            className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                            title="Edit"
                          >
                            <Edit className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => handleDeleteNews(news.id)}
                            className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                            title="Delete"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* ── Create Modal ── */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200 flex items-center justify-between">
              <h2 className="text-xl font-bold text-gray-900">Add News Item</h2>
              <button onClick={() => setShowCreateModal(false)} className="p-2 hover:bg-gray-100 rounded-lg">
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">News Title *</label>
                <input
                  type="text"
                  value={newNews.title}
                  onChange={(e) => setNewNews({ ...newNews, title: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#1d1d1d]"
                  placeholder="Enter news headline…"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Content (Optional)</label>
                <textarea
                  value={newNews.content}
                  onChange={(e) => setNewNews({ ...newNews, content: e.target.value })}
                  rows="3"
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#1d1d1d]"
                  placeholder="Additional details…"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Link (Optional)</label>
                <input
                  type="url"
                  value={newNews.link}
                  onChange={(e) => setNewNews({ ...newNews, link: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#1d1d1d]"
                  placeholder="https://example.com"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Display Order</label>
                  <input
                    type="number"
                    min="0"
                    value={newNews.order}
                    onChange={(e) => setNewNews({ ...newNews, order: parseInt(e.target.value) || 0 })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#1d1d1d]"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Expiry Date</label>
                  <input
                    type="datetime-local"
                    value={newNews.expiry_date}
                    onChange={(e) => setNewNews({ ...newNews, expiry_date: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#1d1d1d]"
                  />
                </div>
              </div>

              <label className="flex items-center space-x-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={newNews.is_breaking}
                  onChange={(e) => setNewNews({ ...newNews, is_breaking: e.target.checked })}
                  className="h-4 w-4 text-[#1d1d1d] rounded border-gray-300 focus:ring-[#1d1d1d]"
                />
                <span className="text-sm font-medium text-gray-700">Mark as Breaking News</span>
              </label>
            </div>

            <div className="p-6 border-t border-gray-200 flex justify-end space-x-3">
              <button
                onClick={() => setShowCreateModal(false)}
                className="px-5 py-2.5 border border-gray-300 text-gray-700 rounded-xl hover:bg-gray-50 font-medium"
              >
                Cancel
              </button>
              <button
                onClick={handleCreateNews}
                disabled={!newNews.title.trim() || createMutation.isPending}
                className="px-5 py-2.5 bg-[#1d1d1d] text-white rounded-xl hover:bg-black disabled:opacity-50 disabled:cursor-not-allowed font-medium flex items-center space-x-2"
              >
                {createMutation.isPending
                  ? <><RefreshCw className="h-4 w-4 animate-spin" /><span>Saving…</span></>
                  : <><Save className="h-4 w-4" /><span>Add News</span></>}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Edit Modal ── */}
      {showEditModal && selectedNews && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200 flex items-center justify-between">
              <h2 className="text-xl font-bold text-gray-900">Edit News Item</h2>
              <button onClick={() => setShowEditModal(false)} className="p-2 hover:bg-gray-100 rounded-lg">
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">News Headline *</label>
                <input
                  type="text"
                  value={selectedNews.title || ''}
                  onChange={(e) => setSelectedNews({ ...selectedNews, title: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#1d1d1d]"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Content</label>
                <textarea
                  value={selectedNews.content || ''}
                  onChange={(e) => setSelectedNews({ ...selectedNews, content: e.target.value })}
                  rows="3"
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#1d1d1d]"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Link</label>
                <input
                  type="url"
                  value={selectedNews.link || ''}
                  onChange={(e) => setSelectedNews({ ...selectedNews, link: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#1d1d1d]"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
                  <select
                    value={selectedNews.status || 'active'}
                    onChange={(e) => setSelectedNews({ ...selectedNews, status: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#1d1d1d]"
                  >
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                    <option value="scheduled">Scheduled</option>
                    <option value="archived">Archived</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Display Order</label>
                  <input
                    type="number"
                    min="0"
                    value={selectedNews.order || 0}
                    onChange={(e) => setSelectedNews({ ...selectedNews, order: parseInt(e.target.value) || 0 })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#1d1d1d]"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Expiry Date</label>
                  <input
                    type="datetime-local"
                    value={selectedNews.expiry_date
                      ? format(new Date(selectedNews.expiry_date), "yyyy-MM-dd'T'HH:mm")
                      : ''}
                    onChange={(e) => setSelectedNews({ ...selectedNews, expiry_date: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#1d1d1d]"
                  />
                </div>
                <div className="space-y-3 pt-7">
                  <label className="flex items-center space-x-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={selectedNews.is_breaking || false}
                      onChange={(e) => setSelectedNews({ ...selectedNews, is_breaking: e.target.checked })}
                      className="h-4 w-4 text-[#1d1d1d] rounded border-gray-300 focus:ring-[#1d1d1d]"
                    />
                    <span className="text-sm font-medium text-gray-700">Breaking News</span>
                  </label>
                  <label className="flex items-center space-x-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={selectedNews.is_ticker_enabled !== false}
                      onChange={(e) => setSelectedNews({ ...selectedNews, is_ticker_enabled: e.target.checked })}
                      className="h-4 w-4 text-[#1d1d1d] rounded border-gray-300 focus:ring-[#1d1d1d]"
                    />
                    <span className="text-sm font-medium text-gray-700">Show in Ticker</span>
                  </label>
                </div>
              </div>
            </div>

            <div className="p-6 border-t border-gray-200 flex justify-end space-x-3">
              <button
                onClick={() => setShowEditModal(false)}
                className="px-5 py-2.5 border border-gray-300 text-gray-700 rounded-xl hover:bg-gray-50 font-medium"
              >
                Cancel
              </button>
              <button
                onClick={handleUpdateNews}
                disabled={updateMutation.isPending || !selectedNews.title}
                className="px-5 py-2.5 bg-[#1d1d1d] text-white rounded-xl hover:bg-black disabled:opacity-50 disabled:cursor-not-allowed font-medium flex items-center space-x-2"
              >
                {updateMutation.isPending
                  ? <><RefreshCw className="h-4 w-4 animate-spin" /><span>Saving…</span></>
                  : <><Save className="h-4 w-4" /><span>Save Changes</span></>}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default NewsTickerManagement;